<template>
  <div class="home">
    <AppNavbar @login-click="goToLogin" @start-click="handleClickIndication" />

    <section class="home-hero">
      <div class="home-hero__inner">
        <div class="home-hero__copy">
          <div class="home-hero__tag">
            <i class="home-hero__tag-dot" />
            Rede proprietária 2Brain · Technology &amp; Talent
          </div>
          <h1 class="home-hero__title">
            Indique profissionais de TI.<br />
            <em>Ganhe de verdade.</em>
          </h1>
          <p class="home-hero__sub">
            Você conhece bons profissionais de tecnologia? Faça sua indicação e receba uma
            recompensa a cada contratação efetivada. Simples, rápido e transparente.
          </p>
          <div class="home-hero__actions">
            <BaseButton variant="primary" size="lg" @click="handleClickIndication">
              Quero indicar agora →
            </BaseButton>
            <BaseButton variant="ghost" size="lg" tag="a" href="#como-funciona">
              Ver como funciona
            </BaseButton>
          </div>
          <div class="home-hero__stats">
            <div class="home-hero__stat">
              <span class="home-hero__stat-val">+50.000</span>
              <span class="home-hero__stat-label">profissionais registrados</span>
            </div>
            <div class="home-hero__stat">
              <span class="home-hero__stat-val">R$ 100M+</span>
              <span class="home-hero__stat-label">pagos em recompensas</span>
            </div>
            <div class="home-hero__stat">
              <span class="home-hero__stat-val">+1.000</span>
              <span class="home-hero__stat-label">profissionais contratados</span>
            </div>
          </div>
        </div>

        <HeroCard
          :steps="[
            { title: 'Cadastre-se gratuitamente', sub: 'Crie sua conta em menos de 2 minutos, sem burocracia.' },
            { title: 'Indique profissionais de TI', sub: 'Compartilhe o perfil de quem você conhece — devs, analistas, PMs e mais.' },
            { title: 'Receba sua recompensa', sub: 'Quando o profissional for contratado, você recebe direto na conta.' },
          ]"
        />
      </div>
    </section>

    <div class="home-logos">
      <span class="home-logos__label">Empresas que contratam via Indicoo</span>
      <div class="home-logos__track">
        <span v-for="(name, index) in logoNames" :key="`${name}-${index}`" class="home-logos__item">
          {{ name }}
        </span>
      </div>
    </div>

    <section id="como-funciona" class="home-section home-section--alt">
      <div class="home-section__header">
        <Eyebrow>Como Funciona</Eyebrow>
        <h2 class="home-section__title">Três passos para começar<br />a ganhar com suas indicações</h2>
        <p class="home-section__sub">
          Sem complicação. Você indica, as empresas fazem o processo seletivo, você acompanha e recebe.
        </p>
      </div>
      <div class="home-steps-grid">
        <StepCard
          v-for="step in steps"
          :key="step.title"
          :num="step.num"
          :icon="step.icon"
          :title="step.title"
          :description="step.description"
        />
      </div>
    </section>

    <section id="beneficios" class="home-section">
      <div class="home-section__header">
        <Eyebrow>Por que Indicar</Eyebrow>
        <h2 class="home-section__title">A forma mais simples de<br />monetizar sua rede profissional</h2>
        <p class="home-section__sub">
          Você já ajuda pessoas a encontrar empregos. Com o Indicoo, você é recompensado por isso.
        </p>
      </div>
      <div class="home-benefits-grid">
        <BenefitCard
          v-for="benefit in benefits"
          :key="benefit.title"
          :icon="benefit.icon"
          :title="benefit.title"
          :description="benefit.description"
        />
      </div>
    </section>

    <section id="ganhos" class="home-section home-section--alt">
      <div class="home-earn">
        <div class="home-earn__copy">
          <Eyebrow>Quanto Você Ganha</Eyebrow>
          <h2 class="home-section__title">Recompensas por nível de<br />perfil contratado</h2>
          <p class="home-section__sub home-section__sub--left">
            O valor da recompensa é definido pelo nível de senioridade e especialização do
            profissional indicado. Perfis mais raros, recompensas maiores.
          </p>
          <div class="home-earn__badges">
            <div v-for="badge in earnBadges" :key="badge.title" class="home-earn-badge">
              <span class="home-earn-badge__icon">{{ badge.icon }}</span>
              <div class="home-earn-badge__text">
                <span class="home-earn-badge__title">{{ badge.title }}</span>
                <span class="home-earn-badge__sub">{{ badge.sub }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="home-earn-card">
          <div class="home-earn-card__header">
            <span class="home-earn-card__label">Tabela de recompensas</span>
            <span class="home-earn-card__badge">✓ Atualizada</span>
          </div>
          <div v-for="row in rewardRows" :key="row.role" class="home-earn-card__row">
            <div class="home-earn-card__role-block">
              <span class="home-earn-card__role">{{ row.role }}</span>
              <span class="home-earn-card__level">{{ row.level }}</span>
            </div>
            <span class="home-earn-card__amt">{{ row.amount }}</span>
          </div>
          <p class="home-earn-card__note">
            Valores referenciais. Consulte o time Indicoo para detalhes e tabela completa.
          </p>
        </div>
      </div>
    </section>

    <section id="validacao" class="home-section">
      <div class="home-validation">
        <div class="home-validation__copy">
          <Eyebrow>Processo de Validação</Eyebrow>
          <h2 class="home-section__title">Como sua indicação vira<br />uma contratação</h2>
          <p class="home-section__sub home-section__sub--left">
            A empresa assume toda a parte de triagem, entrevistas e negociação. Você só precisa
            indicar — o resto é com a gente.
          </p>
        </div>

        <div class="home-pipeline">
          <div v-for="(item, index) in pipeline" :key="item.title" class="home-pipeline__item">
            <div class="home-pipeline__dot">
              <span class="home-pipeline__circle">{{ String(index + 1).padStart(2, '0') }}</span>
              <span v-if="index < pipeline.length - 1" class="home-pipeline__line" />
            </div>
            <div class="home-pipeline__content">
              <h3 class="home-pipeline__title">{{ item.title }}</h3>
              <p class="home-pipeline__desc">{{ item.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="depoimentos" class="home-section home-section--alt">
      <div class="home-section__header">
        <Eyebrow>Depoimentos</Eyebrow>
        <h2 class="home-section__title">Quem já indicou,<br />recomenda de novo</h2>
      </div>
      <div class="home-testimonials-grid">
        <TestimonialCard
          v-for="testimonial in testimonials"
          :key="testimonial.name"
          :quote="testimonial.quote"
          :name="testimonial.name"
          :role="testimonial.role"
        />
      </div>
    </section>

    <section id="cadastro" class="home-cta">
      <div class="home-cta__inner">
        <Eyebrow>Comece agora</Eyebrow>
        <h2 class="home-cta__title">
          Sua rede profissional<br />vale muito mais do que você imagina.
        </h2>
        <p class="home-cta__sub">
          Cadastre-se gratuitamente, comece a indicar hoje e receba sua primeira recompensa em
          até 30 dias após a contratação.
        </p>
        <BaseButton variant="primary" size="lg" @click="handleClickIndication">
          Criar minha conta grátis →
        </BaseButton>
        <p class="home-cta__note">Gratuito · Sem mensalidade · Sem compromisso</p>
      </div>
    </section>

    <AppFooter />
  </div>
</template>

<script>
import { onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import AppFooter from '../components/ui/AppFooter.vue';
import AppNavbar from '../components/ui/AppNavbar.vue';
import BaseButton from '../components/ui/BaseButton.vue';
import BenefitCard from '../components/ui/BenefitCard.vue';
import Eyebrow from '../components/ui/Eyebrow.vue';
import HeroCard from '../components/ui/HeroCard.vue';
import StepCard from '../components/ui/StepCard.vue';
import TestimonialCard from '../components/ui/TestimonialCard.vue';

const LOGO_NAMES = [
  'Casas Bahia', 'Evertec · SINQIA', 'CNN Brasil', 'Sem Parar', 'IBOPE', 'PwC',
  'Creditas', 'TOTVS', 'Atvos', 'Estapar', 'Grupo Cosan', 'Pearson',
];

const STEPS = [
  {
    num: 1,
    icon: '🧑‍💻',
    title: 'Cadastre-se na plataforma',
    description: 'Crie sua conta gratuitamente com nome, e-mail e uma breve descrição. Nenhuma taxa, nenhuma assinatura.',
  },
  {
    num: 2,
    icon: '🔗',
    title: 'Indique profissionais de TI',
    description: 'Conhece um dev, analista, arquiteto ou DBA incrível? Cadastre o perfil diretamente ou compartilhe o link de indicação.',
  },
  {
    num: 3,
    icon: '💰',
    title: 'Receba sua recompensa',
    description: 'Quando o profissional for aprovado e começar a trabalhar em uma vaga Indicoo, você recebe automaticamente. Transparência total.',
  },
];

const BENEFITS = [
  {
    icon: '⚡',
    title: 'Rápido e sem burocracia',
    description: 'Indique em menos de 5 minutos. Nosso time assume todo o processo de triagem, entrevista e alocação — você só acompanha.',
  },
  {
    icon: '💵',
    title: 'Recompensas reais',
    description: 'Receba em dinheiro por cada profissional contratado. O valor varia de acordo com o nível do perfil indicado.',
  },
  {
    icon: '📡',
    title: 'Acompanhamento em tempo real',
    description: 'Visualize o status de cada indicação em sua conta — da submissão ao pagamento, com total transparência.',
  },
  {
    icon: '🔒',
    title: 'Suas indicações protegidas',
    description: 'Cada indicação fica registrada com seu nome. Não há risco de perda de crédito — a plataforma garante rastreabilidade total.',
  },
  {
    icon: '🤝',
    title: 'Rede confiável',
    description: 'O Indicoo é a rede proprietária da 2Brain, empresa com +6 anos de mercado e mais de 350 profissionais alocados.',
  },
  {
    icon: '♾️',
    title: 'Sem limite de indicações',
    description: 'Indique quantos profissionais quiser. Cada contratação gera uma nova recompensa. Quanto mais você indica, mais você ganha.',
  },
];

const EARN_BADGES = [
  {
    icon: '📅',
    title: 'Pagamento garantido',
    sub: 'Após o 1º mês de alocação confirmada',
  },
  {
    icon: '🧾',
    title: 'Sem taxa de plataforma',
    sub: 'Você recebe 100% da recompensa anunciada',
  },
  {
    icon: '↩️',
    title: 'Recompensas recorrentes',
    sub: 'Indique o mesmo profissional para novos projetos',
  },
];

const REWARD_ROWS = [
  { role: 'Desenvolvedor / Engineer', level: 'Pleno · Sênior · Tech Lead', amount: 'R$ 300–1.500' },
  { role: 'Analista de Dados / BI', level: 'Pleno · Sênior', amount: 'R$ 500–2.000' },
  { role: 'Business Analyst / PM', level: 'Pleno · Sênior', amount: 'R$ 300–2.000' },
  { role: 'Arquiteto / CTO / VP Eng', level: 'Especialista · Executivo', amount: 'R$ 500–3.000' },
  { role: 'QA / DevOps / DevSecOps', level: 'Pleno · Sênior', amount: 'R$ 200–1.500' },
];

const PIPELINE = [
  {
    title: 'Você registra a indicação',
    description: 'Insira os dados do profissional na plataforma. O processo começa imediatamente.',
  },
  {
    title: '2Brain faz a triagem técnica',
    description: 'Nossa equipe especializada avalia o perfil, skills e fit cultural em até 3 dias úteis.',
  },
  {
    title: 'Apresentação ao cliente',
    description: 'O perfil aprovado é apresentado às empresas com vagas abertas na Indicoo.',
  },
  {
    title: 'Contratação → Recompensa',
    description: 'Após o 1º mês de alocação confirmada, o valor é liberado para você. Sem enrolação.',
  },
];

const TESTIMONIALS = [
  {
    quote: 'Indiquei três pessoas que eu conhecia da faculdade. As três foram contratadas. Recebi uma recompensa por cada uma e ainda fortaleci meu relacionamento com elas. Incrível.',
    name: 'Rafael M.',
    role: 'Tech Lead · São Paulo',
  },
  {
    quote: 'O processo é simples demais. Entrei na plataforma, cadastrei o nome e LinkedIn do colega, e em duas semanas já tinha a confirmação da contratação. O pagamento veio no prazo.',
    name: 'Ana P.',
    role: 'Analista de Dados · Remoto',
  },
  {
    quote: 'Uso o Indicoo há mais de um ano. Já indiquei oito profissionais — cinco foram contratados. A renda extra gerada foi muito relevante. Recomendo para qualquer pessoa com boa rede.',
    name: 'Gustavo L.',
    role: 'Dev Sênior · Curitiba',
  },
];

export default {
  name: 'Home',
  components: {
    AppFooter,
    AppNavbar,
    BaseButton,
    BenefitCard,
    Eyebrow,
    HeroCard,
    StepCard,
    TestimonialCard,
  },
  setup() {
    const store = useStore();
    const router = useRouter();

    onMounted(() => {
      store.dispatch('validateLogged');
    });

    const handleClickIndication = async () => {
      await store.dispatch('setSignupType', { signupType: 1 });
      router.push({ name: 'Login' });
    };

    const goToLogin = () => {
      router.push({ name: 'Login' });
    };

    return {
      logoNames: [...LOGO_NAMES, ...LOGO_NAMES],
      steps: STEPS,
      benefits: BENEFITS,
      earnBadges: EARN_BADGES,
      rewardRows: REWARD_ROWS,
      pipeline: PIPELINE,
      testimonials: TESTIMONIALS,
      handleClickIndication,
      goToLogin,
    };
  },
};
</script>

<style scoped>
.home {
  background: var(--navy-deep);
}

.home-hero {
  padding: 140px 24px 80px;
}

.home-hero__inner {
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 420px;
  align-items: center;
  gap: var(--gap-2xl);
}

.home-hero__copy {
  display: flex;
  flex-direction: column;
  gap: var(--gap-lg);
}

.home-hero__tag {
  display: inline-flex;
  align-items: center;
  gap: var(--gap-sm);
  font-family: var(--font-brand);
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--muted);
}

.home-hero__tag-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--cyan);
  box-shadow: 0 0 8px var(--cyan);
}

.home-hero__title {
  font-family: var(--font-brand);
  font-size: clamp(2.4rem, 5.5vw, 4rem);
  font-weight: 900;
  letter-spacing: -0.03em;
  line-height: 1.15;
  color: var(--white);
}

.home-hero__title em {
  font-style: normal;
  color: var(--cyan);
}

.home-hero__sub {
  max-width: 520px;
  font-family: var(--font-brand);
  font-size: 1.1rem;
  line-height: 1.75;
  color: var(--text);
}

.home-hero__actions {
  display: flex;
  align-items: center;
  gap: var(--gap-md);
  flex-wrap: wrap;
}

.home-hero__stats {
  display: flex;
  gap: var(--gap-xl);
  flex-wrap: wrap;
  margin-top: var(--gap-md);
  padding-top: var(--gap-xl);
  border-top: 1px solid var(--navy-border);
}

.home-hero__stat {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.home-hero__stat-val {
  font-family: var(--font-brand);
  font-size: 1.55rem;
  font-weight: 800;
  color: var(--white);
}

.home-hero__stat-label {
  font-family: var(--font-brand);
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--muted);
}

.home-logos {
  display: flex;
  flex-direction: column;
  gap: var(--gap-md);
  padding: 32px 0;
  background: var(--navy);
  border-top: 1px solid var(--navy-border);
  border-bottom: 1px solid var(--navy-border);
  overflow: hidden;
}

.home-logos__label {
  font-family: var(--font-brand);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--faint);
  text-align: center;
}

.home-logos__track {
  display: flex;
  gap: 52px;
  width: max-content;
  animation: home-marquee 28s linear infinite;
}

@keyframes home-marquee {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
}

.home-logos__item {
  font-family: var(--font-brand);
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--faint);
  white-space: nowrap;
  letter-spacing: 0.04em;
}

.home-section {
  padding: 96px 24px;
  background: var(--navy-deep);
}

.home-section--alt {
  background: var(--navy-mid);
}

.home-section__header {
  max-width: 680px;
  margin: 0 auto 64px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--gap-sm);
  text-align: center;
}

.home-section__title {
  font-family: var(--font-brand);
  font-size: clamp(1.9rem, 4vw, 2.9rem);
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: 1.15;
  color: var(--white);
}

.home-section__sub {
  font-family: var(--font-brand);
  font-size: 1.05rem;
  line-height: 1.7;
  color: var(--muted);
}

.home-section__sub--left {
  text-align: left;
}

.home-steps-grid {
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  background: var(--navy-border);
  border-radius: var(--r-lg);
  overflow: hidden;
}

.home-benefits-grid {
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--gap-lg);
}

.home-earn {
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: var(--gap-2xl);
}

.home-earn__copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--gap-md);
}

.home-earn__badges {
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  margin-top: var(--gap-sm);
}

.home-earn-badge {
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--navy-card);
  border: 1px solid var(--navy-border);
  border-radius: var(--r-md);
  padding: 16px 20px;
}

.home-earn-badge__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  font-size: 1.2rem;
  background: var(--cyan-glow);
  border-radius: var(--r-sm);
}

.home-earn-badge__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.home-earn-badge__title {
  font-family: var(--font-brand);
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--white);
}

.home-earn-badge__sub {
  font-family: var(--font-brand);
  font-size: 0.78rem;
  color: var(--muted);
}

.home-earn-card {
  background: var(--navy-card);
  border: 1px solid var(--navy-border);
  border-radius: var(--r-lg);
  padding: 40px;
}

.home-earn-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--gap-lg);
}

.home-earn-card__label {
  font-family: var(--font-brand);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--muted);
}

.home-earn-card__badge {
  font-family: var(--font-brand);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--cyan);
  background: var(--cyan-glow);
  border: 1px solid var(--cyan-glow);
  border-radius: var(--r-pill);
  padding: 4px 12px;
}

.home-earn-card__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gap-md);
  padding: 18px 0;
  border-bottom: 1px solid var(--navy-border);
}

.home-earn-card__row:last-of-type {
  border-bottom: none;
}

.home-earn-card__role-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.home-earn-card__role {
  font-family: var(--font-brand);
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--white);
}

.home-earn-card__level {
  font-family: var(--font-brand);
  font-size: 0.78rem;
  color: var(--muted);
}

.home-earn-card__amt {
  flex-shrink: 0;
  font-family: var(--font-brand);
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--cyan);
}

.home-earn-card__note {
  margin-top: var(--gap-lg);
  padding: 14px;
  background: var(--cyan-glow);
  border-radius: var(--r-sm);
  font-family: var(--font-brand);
  font-size: 0.78rem;
  color: var(--muted);
  text-align: center;
}

.home-validation {
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: var(--gap-2xl);
}

.home-validation__copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--gap-sm);
}

.home-pipeline {
  display: flex;
  flex-direction: column;
}

.home-pipeline__item {
  display: flex;
  gap: 28px;
  padding: 28px 0;
  border-bottom: 1px solid var(--navy-border);
}

.home-pipeline__item:last-child {
  border-bottom: none;
}

.home-pipeline__dot {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding-top: 4px;
}

.home-pipeline__circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--cyan-glow);
  border: 2px solid var(--cyan);
  font-family: var(--font-brand);
  font-size: 0.8rem;
  font-weight: 800;
  color: var(--cyan);
}

.home-pipeline__line {
  flex: 1;
  width: 2px;
  margin-top: 6px;
  background: linear-gradient(to bottom, var(--cyan), transparent);
  opacity: 0.2;
}

.home-pipeline__title {
  font-family: var(--font-brand);
  font-size: 1rem;
  font-weight: 700;
  color: var(--white);
  margin-bottom: 6px;
}

.home-pipeline__desc {
  font-family: var(--font-brand);
  font-size: 0.875rem;
  line-height: 1.65;
  color: var(--muted);
}

.home-testimonials-grid {
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--gap-lg);
}

.home-cta {
  position: relative;
  padding: 120px 24px;
  background: var(--navy-mid);
  text-align: center;
  overflow: hidden;
}

.home-cta__inner {
  position: relative;
  z-index: 1;
  max-width: 680px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--gap-md);
}

.home-cta__title {
  font-family: var(--font-brand);
  font-size: clamp(1.9rem, 4vw, 2.9rem);
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: 1.15;
  color: var(--white);
}

.home-cta__sub {
  font-family: var(--font-brand);
  font-size: 1.08rem;
  line-height: 1.7;
  color: var(--muted);
  margin-bottom: var(--gap-sm);
}

.home-cta__note {
  margin-top: var(--gap-sm);
  font-family: var(--font-brand);
  font-size: 0.78rem;
  color: var(--faint);
}

@media (max-width: 900px) {
  .home-hero__inner {
    grid-template-columns: 1fr;
    gap: var(--gap-2xl);
  }

  .home-steps-grid {
    grid-template-columns: 1fr;
    gap: 1px;
  }

  .home-benefits-grid {
    grid-template-columns: 1fr 1fr;
  }

  .home-earn,
  .home-validation {
    grid-template-columns: 1fr;
    gap: var(--gap-xl);
  }

  .home-testimonials-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .home-hero__stats {
    flex-direction: column;
    gap: var(--gap-lg);
  }

  .home-benefits-grid {
    grid-template-columns: 1fr;
  }

  .home-section {
    padding: 64px 24px;
  }
}
</style>
